import * as Sentry from '@sentry/react'

const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

const cacheKey = (options: Intl.DateTimeFormatOptions, timeZone?: string): string => {
  const config = Object.entries(options)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([option, value]) => `${option}:${value}`)
    .join(',')
  return `${config}|${timeZone ?? ''}`
}

const createFormatter = (options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat => {
  if (!timeZone) {
    return new Intl.DateTimeFormat('en-US', options)
  }
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone })
  } catch (error) {
    Sentry.captureException(error, { extra: { timeZone } })
    return new Intl.DateTimeFormat('en-US', options)
  }
}

export const getDateTimeFormatter = (options: Intl.DateTimeFormatOptions, timeZone?: string): Intl.DateTimeFormat => {
  const key = cacheKey(options, timeZone)
  const cached = FORMATTER_CACHE.get(key)
  if (cached) {
    return cached
  }
  const formatter = createFormatter(options, timeZone)
  FORMATTER_CACHE.set(key, formatter)
  return formatter
}

const CURRENCY_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>()

const getCurrencyFormatter = (currency: string, fractionDigits: number): Intl.NumberFormat => {
  const key = `${currency}|${fractionDigits}`
  const cached = CURRENCY_FORMATTER_CACHE.get(key)
  if (cached) {
    return cached
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
  CURRENCY_FORMATTER_CACHE.set(key, formatter)
  return formatter
}

export const formatCurrencyAmount = (value: number, currency = 'USD'): string =>
  Number.isFinite(value) ? getCurrencyFormatter(currency, Number.isInteger(value) ? 0 : 2).format(value) : ''

const CANONICAL_TIMEZONE_CACHE = new Map<string, string | null>()

const resolveTimezone = (timeZone: string): string | null => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

export const canonicalTimezone = (timeZone: string): string | null => {
  const cached = CANONICAL_TIMEZONE_CACHE.get(timeZone)
  if (cached !== undefined) {
    return cached
  }
  const canonical = resolveTimezone(timeZone)
  CANONICAL_TIMEZONE_CACHE.set(timeZone, canonical)
  return canonical
}

export const isSameTimezone = (a: string, b: string): boolean => {
  if (a === b) {
    return true
  }
  const canonicalA = canonicalTimezone(a)
  const canonicalB = canonicalTimezone(b)
  return canonicalA !== null && canonicalB !== null && canonicalA === canonicalB
}
